(() => {
  'use strict';

  if (!window.matchMedia('(max-width: 767px)').matches) return;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const text = (element) => (element?.textContent || '').replace(/\s+/g, ' ').trim();

  function enhanceComparisonAccordion(table) {
    if (!document.body.classList.contains('page-shop') || !table.classList.contains('comp-table')) return;

    const rows = [...table.querySelectorAll('tbody tr')];
    table.classList.add('mx-comparison-accordion');

    const setOpen = (selectedRow) => {
      rows.forEach((row) => {
        const isOpen = row === selectedRow && !row.classList.contains('is-open');
        row.classList.toggle('is-open', isOpen);
        row.querySelector(':scope > :first-child')?.setAttribute('aria-expanded', String(isOpen));
      });
    };

    rows.forEach((row) => {
      const heading = row.querySelector(':scope > :first-child');
      if (!heading) return;

      heading.setAttribute('role', 'button');
      heading.setAttribute('tabindex', '0');
      heading.setAttribute('aria-expanded', 'false');
      heading.setAttribute('aria-label', `${text(heading)} comparison`);
      heading.addEventListener('click', () => setOpen(row));
      heading.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        setOpen(row);
      });
    });
  }

  function enhanceTables() {
    const selectors = [
      'table.comp-table',
      'table.comparison-table',
      '.comparison-table table',
      '.comparison-table-wrapper table'
    ];

    document.querySelectorAll(selectors.join(',')).forEach((table) => {
      if (table.dataset.mxReady === 'true') return;

      const headers = [...table.querySelectorAll('thead th')].map(text);
      if (!headers.length) return;

      table.classList.add('mx-card-table');
      table.dataset.mxReady = 'true';

      table.querySelectorAll('tbody tr').forEach((row) => {
        [...row.children].forEach((cell, index) => {
          cell.dataset.mxLabel = headers[index] || (index === 0 ? 'Feature' : `Option ${index}`);
        });
      });

      enhanceComparisonAccordion(table);
    });
  }

  function mountRail(selector, variant, options = {}) {
    document.querySelectorAll(selector).forEach((rail) => {
      if (rail.dataset.mxRail === 'true') return;

      const cards = [...rail.children].filter((child) => child.nodeType === Node.ELEMENT_NODE);
      if (cards.length < 2) return;

      rail.dataset.mxRail = 'true';
      rail.classList.add('mx-rail', `mx-rail--${variant}`);
      rail.setAttribute('role', 'region');
      rail.setAttribute('aria-label', options.label || 'Scrollable content');
      rail.setAttribute('aria-roledescription', 'carousel');

      let currentIndex = 0;
      const scrollToIndex = (index) => {
        currentIndex = (index + cards.length) % cards.length;
        const card = cards[currentIndex];
        const left = card.offsetLeft - (rail.clientWidth - card.offsetWidth) / 2;
        rail.scrollTo({ left, behavior: reduceMotion ? 'auto' : 'smooth' });
      };

      const guide = document.createElement('div');
      guide.className = 'mx-rail-guide';
      guide.setAttribute('aria-hidden', 'true');

      const label = document.createElement('span');
      label.textContent = options.label || 'Swipe to explore';

      const progress = document.createElement('span');
      progress.className = 'mx-rail-progress';
      const progressFill = document.createElement('span');
      progress.append(progressFill);
      guide.append(label, progress);
      rail.insertAdjacentElement('afterend', guide);

      let dots = [];
      let dotsWrap = null;
      if (options.dots) {
        dotsWrap = document.createElement('div');
        dotsWrap.className = 'mx-rail-dots';
        dotsWrap.setAttribute('aria-label', 'Choose slide');

        dots = cards.map((card, index) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'mx-rail-dot';
          dot.setAttribute('aria-label', `Go to item ${index + 1}`);
          dot.addEventListener('click', () => scrollToIndex(index));
          dotsWrap.append(dot);
          return dot;
        });

        guide.insertAdjacentElement('afterend', dotsWrap);
      }

      let controls = null;
      let autoplayButton = null;
      let autoplayTimer = null;
      let autoplayInView = false;
      let autoplayPaused = false;

      const stopAutoplay = () => {
        window.clearInterval(autoplayTimer);
        autoplayTimer = null;
      };

      const syncAutoplay = () => {
        stopAutoplay();
        if (!options.autoplay || reduceMotion || autoplayPaused || !autoplayInView || document.hidden) return;
        autoplayTimer = window.setInterval(() => scrollToIndex(currentIndex + 1), options.autoplay);
      };

      const updateAutoplayButton = () => {
        if (!autoplayButton) return;
        autoplayButton.textContent = autoplayPaused ? '\u25b6' : '\u275a\u275a';
        autoplayButton.setAttribute('aria-label', autoplayPaused ? 'Play carousel' : 'Pause carousel');
      };

      if (options.controls) {
        controls = document.createElement('div');
        controls.className = 'mx-rail-controls';

        const previous = document.createElement('button');
        previous.type = 'button';
        previous.className = 'mx-rail-control';
        previous.textContent = '\u2190';
        previous.setAttribute('aria-label', 'Previous slide');
        previous.addEventListener('click', () => scrollToIndex(currentIndex - 1));

        controls.append(previous);

        if (options.autoplay && !reduceMotion) {
          autoplayButton = document.createElement('button');
          autoplayButton.type = 'button';
          autoplayButton.className = 'mx-rail-control mx-rail-control--autoplay';
          autoplayButton.addEventListener('click', () => {
            autoplayPaused = !autoplayPaused;
            updateAutoplayButton();
            syncAutoplay();
          });
          controls.append(autoplayButton);
          updateAutoplayButton();
        }

        const next = document.createElement('button');
        next.type = 'button';
        next.className = 'mx-rail-control';
        next.textContent = '\u2192';
        next.setAttribute('aria-label', 'Next slide');
        next.addEventListener('click', () => scrollToIndex(currentIndex + 1));
        controls.append(next);

        (dotsWrap || guide).insertAdjacentElement('afterend', controls);
      }

      let ticking = false;
      const update = () => {
        ticking = false;
        const maxScroll = Math.max(1, rail.scrollWidth - rail.clientWidth);
        const percentage = Math.max(0, Math.min(1, rail.scrollLeft / maxScroll));
        const minimum = Math.min(0.5, 1 / cards.length);
        progressFill.style.transform = `scaleX(${minimum + percentage * (1 - minimum)})`;

        const center = rail.scrollLeft + rail.clientWidth / 2;
        let activeIndex = 0;
        let activeDistance = Infinity;

        cards.forEach((card, index) => {
          const cardCenter = card.offsetLeft + card.offsetWidth / 2;
          const distance = Math.abs(center - cardCenter);
          if (distance < activeDistance) {
            activeDistance = distance;
            activeIndex = index;
          }
        });

        currentIndex = activeIndex;
        cards.forEach((card, index) => {
          const isActive = index === activeIndex;
          card.classList.toggle('is-active', isActive);
          if (isActive) {
            card.setAttribute('aria-current', 'true');
          } else {
            card.removeAttribute('aria-current');
          }
        });
        if (dots.length) {
          dots.forEach((dot, index) => dot.classList.toggle('is-active', index === activeIndex));
        }
      };

      rail.addEventListener('scroll', () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(update);
      }, { passive: true });

      window.addEventListener('resize', update, { passive: true });

      if (options.autoplay && !reduceMotion) {
        const observer = new IntersectionObserver((entries) => {
          autoplayInView = Boolean(entries[0]?.isIntersecting);
          syncAutoplay();
        }, { threshold: 0.35 });

        observer.observe(rail);
        rail.addEventListener('pointerdown', stopAutoplay, { passive: true });
        rail.addEventListener('pointerup', syncAutoplay, { passive: true });
        rail.addEventListener('focusin', stopAutoplay);
        rail.addEventListener('focusout', syncAutoplay);
        document.addEventListener('visibilitychange', syncAutoplay);
      }

      update();
    });
  }

  function mountNativeCarouselGuide(rootSelector, slideSelector, dotSelector, labelText) {
    const root = document.querySelector(rootSelector);
    if (!root || root.dataset.mxNativeGuide === 'true') return;

    const slides = [...root.querySelectorAll(slideSelector)];
    const dots = [...document.querySelectorAll(dotSelector)];
    if (slides.length < 2 || dots.length !== slides.length) return;

    root.dataset.mxNativeGuide = 'true';
    root.setAttribute('role', 'region');
    root.setAttribute('aria-roledescription', 'carousel');
    root.setAttribute('aria-label', labelText);

    const guide = document.createElement('div');
    guide.className = 'mx-rail-guide mx-native-guide';
    guide.setAttribute('aria-hidden', 'true');

    const label = document.createElement('span');
    label.textContent = labelText;

    const progress = document.createElement('span');
    progress.className = 'mx-rail-progress';
    const progressFill = document.createElement('span');
    progress.append(progressFill);
    guide.append(label, progress);
    root.insertAdjacentElement('afterend', guide);

    const update = () => {
      const activeIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains('active')));
      progressFill.style.transform = `scaleX(${(activeIndex + 1) / slides.length})`;
    };

    new MutationObserver(update).observe(root, {
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });

    let startX = 0;
    let startY = 0;
    root.addEventListener('touchstart', (event) => {
      const touch = event.changedTouches[0];
      startX = touch.clientX;
      startY = touch.clientY;
    }, { passive: true });

    root.addEventListener('touchend', (event) => {
      const touch = event.changedTouches[0];
      const deltaX = touch.clientX - startX;
      const deltaY = touch.clientY - startY;
      if (Math.abs(deltaX) < 42 || Math.abs(deltaX) <= Math.abs(deltaY)) return;

      const activeIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains('active')));
      const nextIndex = (activeIndex + (deltaX < 0 ? 1 : -1) + slides.length) % slides.length;
      dots[nextIndex].click();
    }, { passive: true });

    update();
  }

  function enhanceSelectedRails() {
    const rails = [
      ['.template-product .native-product__media', 'product-media', { label: 'Swipe through product images' }],
      ['.template-index .batch-grid', 'batch', { label: 'Swipe through batch records' }],
      ['.template-index .expert-track', 'story', { label: 'Auto-rotating expert stories', controls: true, autoplay: 4500 }],
      ['.page-shop .expert-grid', 'story', { label: 'Expert perspectives' }],
      ['.page-shop .influencer-grid', 'story', { label: 'Swipe through creator reviews' }],
      ['.page-shop .influencer-videos', 'portrait', { label: 'Swipe to watch' }],
      ['.page-product-2plus2 .reviews-grid', 'story', { label: 'Customer stories' }],
      ['.page-product-2plus2 .related-grid', 'product', { label: 'Explore the routine' }],
      ['.page-learn .topics-grid', 'story', { label: 'Swipe through learning topics' }],
      ['.page-providers .tiers-grid', 'tier', { label: 'Compare programs' }],
      ['.page-about .team-grid', 'portrait', { label: 'Meet the team' }]
    ];

    rails.forEach(([selector, variant, options]) => mountRail(selector, variant, options));

    mountNativeCarouselGuide(
      '.page-shop #rrCarousel',
      '.rr-slide',
      '.page-shop #rrDots .rr-dot',
      'Swipe through real results'
    );
    mountNativeCarouselGuide(
      '.page-results #rr2Carousel',
      '.rr2-slide',
      '.page-results #rr2Dots .rr2-dot',
      'Swipe through weekly results'
    );
  }

  function enhanceShopProduct() {
    const shop = document.querySelector('.page-shop');
    if (!shop) return;

    const features = shop.querySelector('.pdp-features');
    if (features && !features.closest('.mx-shop-highlights')) {
      const details = document.createElement('details');
      details.className = 'mx-shop-highlights';

      const summary = document.createElement('summary');
      summary.textContent = 'What is inside your kit';

      features.before(details);
      details.append(summary, features);
    }

    if (!shop.querySelector('.option-btn.active')) {
      shop.querySelector('#kit-2plus2')?.click();
    }
  }

  function simplifyMobileCtas() {
    const selectors = [
      '.template-index .ba-cta a',
      '.template-index .batch-cta a'
    ];

    document.querySelectorAll(selectors.join(',')).forEach((link) => {
      link.textContent = text(link).replace(/\s*\u2192\s*$/, '');
      link.classList.add('mx-mobile-cta');
    });
  }

  function enhanceIconMarquees() {
    const selectors = [
      '.template-index .awards-inner',
      '.page-results .results-trust-inner',
      '.page-providers .trusted-grid'
    ];

    document.querySelectorAll(selectors.join(',')).forEach((marquee) => {
      if (marquee.dataset.mxMarquee === 'true') return;

      const items = [...marquee.children];
      if (items.length < 2) return;

      marquee.dataset.mxMarquee = 'true';
      marquee.classList.add('mx-icon-marquee');
      items.forEach((item) => {
        const clone = item.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        marquee.append(clone);
      });
    });
  }

  function enhanceFooter() {
    document.querySelectorAll('.footer-grid, .footer-columns').forEach((footer) => {
      const columns = [...footer.querySelectorAll(':scope > .footer-col, :scope > .footer-column')];
      const interactiveColumns = columns.filter((column) => column.querySelector(':scope > h3, :scope > h4'));
      if (!interactiveColumns.length) return;

      footer.classList.add('mx-footer-ready');

      interactiveColumns.forEach((column, index) => {
        const heading = column.querySelector(':scope > h3, :scope > h4');
        const toggle = () => {
          const isOpen = column.classList.toggle('is-open');
          heading.setAttribute('aria-expanded', String(isOpen));
        };

        heading.setAttribute('role', 'button');
        heading.setAttribute('tabindex', '0');
        heading.setAttribute('aria-expanded', 'false');
        heading.addEventListener('click', toggle);
        heading.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          toggle();
        });

        if (index === 0 && interactiveColumns.length <= 2) {
          column.classList.add('is-open');
          heading.setAttribute('aria-expanded', 'true');
        }
      });
    });
  }

  function enhanceSafetyCards() {
    document.querySelectorAll('.page-trust .safety-grid').forEach((grid) => {
      const cards = [...grid.querySelectorAll(':scope > .safety-card')];
      if (!cards.length) return;

      grid.classList.add('mx-safety-ready');

      const setOpen = (selected) => {
        cards.forEach((card) => {
          const isOpen = card === selected;
          card.classList.toggle('is-open', isOpen);
          card.setAttribute('aria-expanded', String(isOpen));
        });
      };

      cards.forEach((card) => {
        card.setAttribute('role', 'button');
        card.setAttribute('tabindex', '0');
        card.addEventListener('click', () => setOpen(card));
        card.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          setOpen(card);
        });
      });

      setOpen(cards[0]);
    });
  }

  function initialise() {
    enhanceTables();
    enhanceSelectedRails();
    enhanceShopProduct();
    simplifyMobileCtas();
    enhanceIconMarquees();
    enhanceFooter();
    enhanceSafetyCards();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialise, { once: true });
  } else {
    initialise();
  }
})();
